import * as React from "react";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  SortingState,
  RowSelectionState,
  OnChangeFn,
  Row,
} from "@tanstack/react-table";
import { PaginationTwo } from "components/pagination-two";
import { PaginationProps } from "components/pagination";

// Allow table columns to declare their cell/header width class via meta
declare module "@tanstack/react-table" {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData, TValue> {
    /** Tailwind className applied to both the header cell and body cell */
    className?: string;
    /** Tailwind className applied only to the header cell (overrides className) */
    headerClassName?: string;
    /** Tailwind className applied only to the body cell (overrides className) */
    cellClassName?: string;
    /** data-tour-id placed on the <th> for product tour targeting */
    tourId?: string;
  }
}

interface DataTableProps<TData> {
  columns: ColumnDef<TData, unknown>[];
  data: TData[];
  isLoading?: boolean;
  emptyState?: React.ReactNode;
  pagination?: PaginationProps;
  onRowClick?: (row: TData, rowIndex: number) => void;
  /** Called with the full Row object — useful when you need row.index */
  onRowClickFull?: (row: Row<TData>) => void;
  /** Return false to make a specific row non-clickable */
  getRowClickable?: (row: TData) => boolean;
  // Controlled row selection
  rowSelection?: RowSelectionState;
  onRowSelectionChange?: OnChangeFn<RowSelectionState>;
  getRowId?: (row: TData) => string;
  // Controlled sorting
  sorting?: SortingState;
  onSortingChange?: OnChangeFn<SortingState>;
  enableSorting?: boolean;
  /** Extra className appended to each body row's default classes */
  rowClassName?: string | ((row: TData, index: number) => string);
  /**
   * Fully replaces the default <tr> classes when provided.
   * Strip flex/padding — those belong on cells via cellBaseClassName.
   */
  rowBaseClassName?: string | ((row: TData, index: number) => string);
  /** Applied to the header <tr> (background, font, color — inherited by <th>) */
  headerClassName?: string;
  /** Base className for every <td>. Default: "px-4 py-3" */
  cellBaseClassName?: string;
  /** Base className for every <th>. Default: "px-4 py-3 text-left" */
  headerCellClassName?: string;
  /** Extra className applied to the outer container */
  containerClassName?: string;
}

const SKELETON_ROWS = 5;

// Default <tr> styling for card-shadow tables (no rowBaseClassName)
const DEFAULT_ROW_CLASS =
  "bg-white shadow-[1px_17px_44px_0px_rgba(3,2,41,0.07)] [&>td:first-child]:rounded-l-[10px] [&>td:last-child]:rounded-r-[10px]";

const DEFAULT_CELL_CLASS = "px-4 py-3";
const DEFAULT_HEADER_CELL_CLASS = "px-4 py-3 text-left";

function DataTable<TData>({
  columns,
  data,
  isLoading = false,
  emptyState,
  pagination,
  onRowClick,
  onRowClickFull,
  getRowClickable,
  rowSelection,
  onRowSelectionChange,
  getRowId,
  sorting,
  onSortingChange,
  enableSorting = false,
  rowClassName,
  rowBaseClassName,
  headerClassName,
  cellBaseClassName,
  headerCellClassName,
  containerClassName,
}: DataTableProps<TData>) {
  const tableState: Record<string, unknown> = {};
  if (rowSelection !== undefined) tableState.rowSelection = rowSelection;
  if (sorting !== undefined) tableState.sorting = sorting;

  const table = useReactTable({
    data,
    columns,
    state: tableState as Parameters<typeof useReactTable>[0]["state"],
    getCoreRowModel: getCoreRowModel(),
    ...(enableSorting && { getSortedRowModel: getSortedRowModel() }),
    ...(rowSelection !== undefined && {
      onRowSelectionChange,
      enableRowSelection: true,
    }),
    ...(onSortingChange && { onSortingChange }),
    ...(getRowId && { getRowId }),
    enableSorting,
    manualSorting: !!onSortingChange,
  });

  // Card-shadow tables need border-separate so box-shadow on <tr> renders correctly
  // and border-spacing-y provides the row gap. Striped/bordered tables use collapse.
  const isCardShadow = rowBaseClassName === undefined;
  const tableClass = isCardShadow
    ? "w-full border-separate border-spacing-y-2.5"
    : "w-full border-collapse";

  const baseCellCls = cellBaseClassName ?? DEFAULT_CELL_CLASS;
  const baseHeaderCellCls = headerCellClassName ?? DEFAULT_HEADER_CELL_CLASS;

  return (
    <div className={`w-full ${containerClassName ?? ""}`}>
      <table className={tableClass}>
        {/* Header */}
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr
              key={headerGroup.id}
              className={
                headerClassName !== undefined
                  ? headerClassName
                  : "text-[13px] font-medium text-neutral-500"
              }
            >
              {headerGroup.headers.map((header) => {
                const meta = header.column.columnDef.meta;
                const colCls = meta?.headerClassName ?? meta?.className ?? "";
                return (
                  <th
                    key={header.id}
                    className={`${baseHeaderCellCls} ${colCls}`}
                    {...(meta?.tourId ? { 'data-tour-id': meta.tourId } : {})}
                    onClick={
                      enableSorting && header.column.getCanSort()
                        ? header.column.getToggleSortingHandler()
                        : undefined
                    }
                    style={
                      enableSorting && header.column.getCanSort()
                        ? { cursor: "pointer" }
                        : undefined
                    }
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                    {header.column.getIsSorted() === "asc" && " ↑"}
                    {header.column.getIsSorted() === "desc" && " ↓"}
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>

        {/* Body */}
        <tbody>
          {isLoading ? (
            Array.from({ length: SKELETON_ROWS }).map((_, i) => (
              <tr key={i}>
                <td colSpan={columns.length} className="p-0 pb-2.5">
                  <div className="animate-pulse bg-gray-100 rounded-[10px] h-16" />
                </td>
              </tr>
            ))
          ) : !data.length && emptyState ? (
            <tr>
              <td colSpan={columns.length}>{emptyState}</td>
            </tr>
          ) : (
            table.getRowModel().rows.map((row, index) => {
              const isClickable =
                (onRowClick !== undefined || onRowClickFull !== undefined) &&
                (getRowClickable ? getRowClickable(row.original) : true);

              const baseClass =
                rowBaseClassName !== undefined
                  ? typeof rowBaseClassName === "function"
                    ? rowBaseClassName(row.original, index)
                    : rowBaseClassName
                  : DEFAULT_ROW_CLASS;

              const extraClass =
                typeof rowClassName === "function"
                  ? rowClassName(row.original, index)
                  : (rowClassName ?? "");

              return (
                <tr
                  key={row.id}
                  className={`${baseClass} ${isClickable ? "cursor-pointer" : ""} ${extraClass}`}
                  onClick={
                    isClickable
                      ? () => {
                          onRowClick?.(row.original, index);
                          onRowClickFull?.(row);
                        }
                      : undefined
                  }
                  role={isClickable ? "button" : undefined}
                >
                  {row.getVisibleCells().map((cell) => {
                    const meta = cell.column.columnDef.meta;
                    const colCls = meta?.cellClassName ?? meta?.className ?? "";
                    return (
                      <td key={cell.id} className={`${baseCellCls} ${colCls}`}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    );
                  })}
                </tr>
              );
            })
          )}
        </tbody>
      </table>

      {/* Pagination */}
      {pagination && <PaginationTwo {...pagination} className="mt-4" />}
    </div>
  );
}

export { DataTable };
export type { DataTableProps };
